/** What a destination says when the interface exists and the backend does not.
 *
 * The rule this product applies everywhere is **no fabricated data**: a
 * convincing mock-up in an ITSM console is worse than an empty panel, because
 * somebody reads a number off it and acts. What changed after the navigation
 * audit is the *shape* of the honesty. "Interface only — no data behind it" is
 * true and useless: it reads as a broken page, and it leaves an administrator
 * with nothing to do and nothing to plan.
 *
 * So this answers the four questions an administrator actually has, in the order
 * they ask them:
 *
 *   1. Can I do anything here today?     → Available now
 *   2. What is stopping the rest?        → Not yet available
 *   3. What would have to be built?      → Required API
 *   4. What do I get when it is?         → Unlocked by this
 *
 * A screen that answers those is a roadmap entry with a status. It is still not
 * pretending to hold data it does not have.
 *
 * `children` is where a screen puts whatever *is* real — Organizations renders
 * the live read-only list there, because "available now" is more convincing when
 * you can see it.
 */
import { CircleSlash, Plug, ShieldCheck, Sparkles } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../lib/utils'
import { PageHeader } from './layout/AppShell'
import { Badge } from './ui/badge'
import { Card } from './ui/card'

export interface CapabilityStateProps {
  title: string
  /** One line under the title, in the page header. */
  description: string
  /** One sentence: the interface is ready, and which half is not. */
  summary: string
  /** What genuinely works today. An empty list is honest and renders as such. */
  available?: string[]
  /** The backend capability that is absent. */
  missing: string[]
  /** The endpoint(s) this screen needs, named as they would be routed. */
  api: string[]
  /** What becomes possible once the API lands. */
  unlocks?: string[]
  /** Anything real the screen can show today — a live read-only list, say. */
  children?: ReactNode
}

export function CapabilityState({
  title,
  description,
  summary,
  available = [],
  missing,
  api,
  unlocks = [],
  children,
}: CapabilityStateProps) {
  return (
    <>
      <PageHeader
        title={title}
        description={description}
        actions={
          <Badge variant="warning" size="sm">
            Backend pending
          </Badge>
        }
      />

      {/* The headline. Deliberately a statement of readiness, not a failure:
          the UI is built, the API is the outstanding half. */}
      <Card className="border-warning/40 bg-warning/5 p-4">
        <div className="flex gap-3">
          <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-warning/15 text-warning">
            <ShieldCheck className="size-4" aria-hidden />
          </span>
          <div>
            <h2 className="text-sm font-semibold">{title} — interface ready, API pending</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{summary}</p>
          </div>
        </div>
      </Card>

      {children && <div className="mt-4">{children}</div>}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel
          icon={Sparkles}
          tone="success"
          title="Available now"
          empty="Nothing on this screen is served yet. Everything below is outstanding."
          items={available}
        />
        <Panel
          icon={CircleSlash}
          tone="warning"
          title="Not yet available"
          items={missing}
        />
        <Panel
          icon={Plug}
          tone="info"
          title="Required API"
          items={api}
          mono
        />
        <Panel
          icon={ShieldCheck}
          tone="neutral"
          title="Unlocked by this"
          empty="Reserved — no capability is planned against it yet."
          items={unlocks}
        />
      </div>
    </>
  )
}

const TONES = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/15 text-warning',
  info: 'bg-info/10 text-info',
  neutral: 'bg-muted text-muted-foreground',
} as const

function Panel({
  icon: Icon,
  tone,
  title,
  items,
  empty,
  mono = false,
}: {
  icon: LucideIcon
  tone: keyof typeof TONES
  title: string
  items: string[]
  /** Shown instead of the list when there is nothing to say. Saying "none" is
   *  the point — a hidden panel would read as an oversight. */
  empty?: string
  mono?: boolean
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2.5">
        <span className={cn('grid size-7 shrink-0 place-items-center rounded-md', TONES[tone])}>
          <Icon className="size-3.5" aria-hidden />
        </span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>

      {items.length === 0 ? (
        <p className="mt-2.5 text-xs text-muted-foreground">{empty ?? 'Nothing recorded.'}</p>
      ) : (
        <ul className="mt-2.5 space-y-1.5">
          {items.map((item) => (
            <li key={item} className="flex gap-2 text-xs text-muted-foreground">
              <span className="mt-1 size-1 shrink-0 rounded-full bg-border" aria-hidden />
              <span className={cn(mono && 'numeric font-mono text-[0.6875rem] leading-4')}>
                {item}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
